// Точка анкеты — как в Wolt/Bolt (26.10.2026). В форме — маленький
// предпросмотр и кнопка «Xəritədə seç»; по нажатию — карта на весь экран:
// двигается одним пальцем (gestureHandling greedy), пин стоит в центре экрана,
// двигается карта; сверху поиск адреса (Google Places, PlaceAutocompleteElement),
// внизу «Mənim yerim» и «Bu yeri seç». Раньше карта была в форме и на iPhone
// двигалась только двумя пальцами (один палец листал страницу).
//
// Пока карта отдалена (zoom < PIN_MIN_ZOOM), «Bu yeri seç» точку не ставит, а
// приближает: 14.10.2026 первый же выбор по карте всего Баку ставил пин в
// другом районе, и человек этого не замечал. Нужен режиму building: travel и
// online точку не хранят. Скрипт Google Maps грузится ТОЛЬКО здесь и только
// когда блок виден на экране (лимит бесплатных загрузок), ключ приходит с
// сервера (переменная воркера GOOGLE_MAPS_BROWSER_KEY). Нет ключа или карта не
// загрузилась — остаётся поле адреса текстом, заявку можно отправить.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type LatLng = { lat: number; lng: number };

/* eslint-disable @typescript-eslint/no-explicit-any */
type GoogleNS = any;

let loading: Promise<GoogleNS> | null = null;
export function loadMaps(key: string, lang: string): Promise<GoogleNS> {
  const w = window as any;
  if (w.google?.maps?.importLibrary) return Promise.resolve(w.google);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const cb = "__ytMapsReady";
    w[cb] = () => resolve(w.google);
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&libraries=places&language=${lang}&region=AZ&callback=${cb}`;
    s.async = true;
    s.onerror = () => {
      loading = null;
      reject(new Error("maps script failed"));
    };
    document.head.appendChild(s);
  });
  return loading;
}

const BAKU: LatLng = { lat: 40.4093, lng: 49.8671 };
/** Ниже этого приближения «Bu yeri seç» только приближает карту, точка не ставится. */
export const PIN_MIN_ZOOM = 16;
const ZOOM_TO = 17;

export type PinLabels = {
  address: string;
  addressPh: string;
  searchPh: string;
  hint: string;
  zoomed: string;
  loading: string;
  noKey: string;
  open: string;
  reopen: string;
  choose: string;
  me: string;
  meFail: string;
  back: string;
  drag: string;
};

export function PinPicker({
  mapsKey,
  lang,
  point,
  address,
  onPoint,
  onAddress,
  onState,
  labels,
  focus,
}: {
  mapsKey: string | null;
  lang: "ru" | "az" | "en";
  point: LatLng | null;
  address: string;
  onPoint: (p: LatLng) => void;
  onAddress: (a: string) => void;
  /** ready — точку можно выбрать; failed — карты нет (нет ключа или Google не загрузился). */
  onState?: (s: "loading" | "ready" | "failed") => void;
  labels: PinLabels;
  /** Город или посёлок выбран в форме — карта переезжает туда; n меняется — едет снова. */
  focus?: { lat: number; lng: number; n: number; zoom?: number } | null;
}) {
  const boxEl = useRef<HTMLDivElement>(null);
  const previewEl = useRef<HTMLDivElement>(null);
  const fullEl = useRef<HTMLDivElement>(null);
  const acEl = useRef<HTMLDivElement>(null);
  const g = useRef<{ google?: any; preview?: any; marker?: any; full?: any }>({});
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "failed">(mapsKey ? "loading" : "failed");
  const [open, setOpen] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [meErr, setMeErr] = useState(false);
  const [locating, setLocating] = useState(false);
  const onPointRef = useRef(onPoint);
  const onAddressRef = useRef(onAddress);
  onPointRef.current = onPoint;
  onAddressRef.current = onAddress;
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  useEffect(() => {
    if (state !== "idle") onStateRef.current?.(state);
  }, [state]);

  const startView = () =>
    point ? { center: point, zoom: ZOOM_TO } : focus ? { center: { lat: focus.lat, lng: focus.lng }, zoom: focus.zoom ?? 13 } : { center: BAKU, zoom: 12 };

  // Карта грузится, только когда блок дошёл до экрана.
  useEffect(() => {
    const el = boxEl.current;
    if (!mapsKey || !el || visible) return;
    const io = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && setVisible(true), { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [mapsKey, visible]);

  // Предпросмотр: маленькая карта без жестов — нажатие открывает экран выбора.
  useEffect(() => {
    if (!mapsKey || !visible || !previewEl.current) return;
    let cancelled = false;
    loadMaps(mapsKey, lang)
      .then(async (google) => {
        if (cancelled || !previewEl.current) return;
        const { Map } = await google.maps.importLibrary("maps");
        const { Marker } = await google.maps.importLibrary("marker");
        await google.maps.importLibrary("places");
        const v = startView();
        const preview = new Map(previewEl.current, {
          ...v,
          gestureHandling: "none",
          disableDefaultUI: true,
          keyboardShortcuts: false,
          clickableIcons: false,
        });
        const marker = new Marker({ map: preview, position: point ?? BAKU, visible: !!point, clickable: false });
        g.current = { ...g.current, google, preview, marker };
        setState("ready");
      })
      .catch(() => !cancelled && setState("failed"));
    return () => {
      cancelled = true;
    };
    // Карта создаётся один раз — дальше её двигают эффекты ниже.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsKey, lang, visible]);

  // Точка или выбранный город — предпросмотр едет туда.
  useEffect(() => {
    const { preview, marker } = g.current;
    if (!preview || state !== "ready") return;
    const v = startView();
    preview.setCenter(v.center);
    preview.setZoom(v.zoom);
    if (point) marker.setPosition(point);
    marker.setVisible(!!point);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point?.lat, point?.lng, focus?.n, state]);

  // Экран выбора: карта создаётся при первом открытии, потом переиспользуется.
  useEffect(() => {
    if (!open || state !== "ready") return;
    const google = g.current.google;
    const v = startView();
    setZoomed(false);
    setMeErr(false);
    if (g.current.full) {
      g.current.full.setCenter(v.center);
      g.current.full.setZoom(v.zoom);
    } else if (fullEl.current) {
      (async () => {
        const { Map } = await google.maps.importLibrary("maps");
        const places = await google.maps.importLibrary("places");
        const full = new Map(fullEl.current, {
          ...v,
          // Один палец двигает карту (а не страницу): экран целиком — карта.
          gestureHandling: "greedy",
          clickableIcons: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          zoomControl: false,
        });
        full.addListener("dragstart", () => setZoomed(false));
        g.current.full = full;
        if (acEl.current && places.PlaceAutocompleteElement) {
          const ac = new places.PlaceAutocompleteElement({ includedRegionCodes: ["az"] });
          ac.setAttribute("placeholder", labels.searchPh);
          ac.style.width = "100%";
          acEl.current.replaceChildren(ac);
          ac.addEventListener("gmp-select", async (ev: any) => {
            try {
              const place = ev.placePrediction.toPlace();
              await place.fetchFields({ fields: ["location", "formattedAddress"] });
              if (place.formattedAddress) onAddressRef.current(place.formattedAddress);
              if (place.location) {
                full.setCenter(place.location);
                full.setZoom(18);
              }
            } catch {
              /* место можно найти и картой */
            }
          });
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, state]);

  // Экран выбора поверх страницы: страница не прокручивается, «назад» телефона закрывает.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    history.pushState({ ytPin: 1 }, "");
    const onPop = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("popstate", onPop);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const close = () => {
    if (history.state?.ytPin) history.back();
    else setOpen(false);
  };

  const choose = () => {
    const full = g.current.full;
    if (!full) return;
    if ((full.getZoom() ?? 0) < PIN_MIN_ZOOM) {
      full.setZoom(ZOOM_TO);
      setZoomed(true);
      return;
    }
    const c = full.getCenter();
    onPointRef.current({ lat: c.lat(), lng: c.lng() });
    close();
  };
  const locate = () => {
    if (!navigator.geolocation) return setMeErr(true);
    setLocating(true);
    setMeErr(false);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        g.current.full?.setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        g.current.full?.setZoom(ZOOM_TO);
      },
      () => {
        setLocating(false);
        setMeErr(true);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  return (
    <div className="tj-pin" ref={boxEl}>
      {mapsKey && state !== "failed" ? (
        <>
          <button type="button" className="tj-map tj-map--preview" onClick={() => state === "ready" && setOpen(true)} aria-label={point ? labels.reopen : labels.open}>
            <div ref={previewEl} className="tj-map__canvas" aria-hidden="true" />
            {state === "loading" && <span className="tj-map__hint">{labels.loading}</span>}
          </button>
          <button type="button" className="hp-btn tj-map__open" disabled={state !== "ready"} onClick={() => setOpen(true)}>
            📍 {point ? labels.reopen : labels.open}
          </button>
        </>
      ) : (
        <p className="tj-note">{labels.noKey}</p>
      )}
      {state !== "ready" && (
        <input
          className="tj-in"
          type="text"
          value={address}
          onChange={(e) => onAddress(e.target.value)}
          placeholder={labels.addressPh}
          aria-label={labels.address}
          autoComplete="street-address"
        />
      )}
      {typeof document !== "undefined" &&
        state === "ready" &&
        createPortal(
          <div className="tj-pinscreen" hidden={!open} role="dialog" aria-modal="true" aria-label={labels.open}>
            <div className="tj-pinscreen__top">
              <button type="button" className="tj-pinscreen__back" onClick={close} aria-label={labels.back}>
                ←
              </button>
              <div ref={acEl} className="tj-ac" />
            </div>
            <div className="tj-pinscreen__map">
              <div ref={fullEl} className="tj-map__canvas" role="application" aria-label={labels.drag} />
              {/* Пин неподвижен: стоит в центре, двигается карта. Остриё — точно в центре. */}
              <svg className="tj-pinscreen__pin" width="40" height="52" viewBox="0 0 40 52" aria-hidden="true">
                <path d="M20 51C20 51 38 31 38 19A18 18 0 0 0 2 19C2 31 20 51 20 51Z" fill="#0F3D3E" stroke="#fff" strokeWidth="2.5" />
                <circle cx="20" cy="19" r="6.5" fill="#F2A541" />
              </svg>
              <span className={`tj-map__hint${zoomed ? " tj-map__hint--zoomed" : ""}`} role="status">
                {zoomed ? labels.zoomed : meErr ? labels.meFail : labels.drag}
              </span>
            </div>
            <div className="tj-pinscreen__bottom">
              <button type="button" className="tj-chip tj-pinscreen__me" onClick={locate} disabled={locating}>
                ◎ {labels.me}
              </button>
              <button type="button" className="hp-btn hp-btn--lg tj-pinscreen__choose" onClick={choose}>
                {labels.choose}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
